'use client';

import { c as _c } from "react/compiler-runtime";
import type { PendingSelection } from '@/components/reviewers/utils';
import { Check, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import type { PrV2Label } from '../shared/pr-v2-metadata';
import { buildPrV2LabelOptions, toLabelHexColor } from './pr-v2-sidebar-model';
import { isPendingSelected, togglePendingSelection } from '@/components/reviewers/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BetterTooltip } from '@/components/ui/tooltip';
import { usePrV2AvailableLabels } from '@/data/v2/pr/use-pr-v2-available-labels';
import { usePrV2UpdateLabels } from '@/data/v2/pr/use-pr-v2-update-labels';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { cn } from '@/lib/utils';
function createEmptyPendingChanges(): PendingSelection {
  return {
    toAdd: new Set(),
    toRemove: new Set()
  };
}

/**
 * The PR page sidebar's label picker. Picks collect while the menu is open and go to GitHub as
 * one change when it closes, as in the reviewer picker.
 */
export function PrV2EditLabelsButton(t0) {
  const $ = _c(73);
  const {
    owner,
    repo,
    number,
    installationId,
    labels,
    onUpdate
  } = t0;
  const [open, setOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(createEmptyPendingChanges);
  let t1;
  if ($[0] !== open) {
    t1 = {
      enabled: open
    };
    $[0] = open;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: availableLabels,
    isError
  } = usePrV2AvailableLabels(owner, repo, number, t1);
  const {
    mutate: updateLabels,
    isPending
  } = usePrV2UpdateLabels(owner, repo, number);
  const hasLabels = labels.length > 0;
  let t2;
  if ($[2] !== labels) {
    t2 = new Set(labels.map(_temp));
    $[2] = labels;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const appliedLabelNames = t2;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== appliedLabelNames || $[5] !== availableLabels || $[6] !== hasLabels || $[7] !== installationId || $[8] !== isError || $[9] !== isPending || $[10] !== labels || $[11] !== number || $[12] !== onUpdate || $[13] !== open || $[14] !== owner || $[15] !== pendingChanges || $[16] !== repo || $[17] !== updateLabels) {
    const options = buildPrV2LabelOptions(labels, availableLabels ?? []);
    const listStatus = availableLabels ? null : isError ? "Could not load labels." : "Loading labels\u2026";
    const actionLabel = hasLabels ? "Edit labels" : "Add labels";
    let t12;
    if ($[32] !== appliedLabelNames) {
      t12 = labelName => {
        const nameKey = labelName.toLowerCase();
        setPendingChanges(previous => togglePendingSelection(previous, nameKey, appliedLabelNames.has(nameKey)));
      };
      $[32] = appliedLabelNames;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    const handleLabelSelect = t12;
    const commitPendingChanges = changes => {
      if (changes.toAdd.size === 0 && changes.toRemove.size === 0) {
        return;
      }
      const add = options.filter(label_0 => changes.toAdd.has(label_0.name.toLowerCase())).map(_temp2);
      const remove = options.filter(label_2 => changes.toRemove.has(label_2.name.toLowerCase())).map(_temp3);
      trackClientEvent("pr_labels_updated", installationId?.toString(), {
        pr_number: number,
        repo: `${owner}/${repo}`,
        labels_added: add.length,
        labels_removed: remove.length
      });
      updateLabels({
        add,
        remove
      });
      onUpdate?.();
    };
    const handleOpenChange = isOpen => {
      if (!isOpen) {
        commitPendingChanges(pendingChanges);
        setPendingChanges(createEmptyPendingChanges());
      }
      setOpen(isOpen);
    };
    T4 = DropdownMenu;
    t9 = open;
    t10 = handleOpenChange;
    let t13;
    if ($[34] !== hasLabels) {
      t13 = hasLabels ? <Pencil className="size-4" /> : <Plus className="size-4" />;
      $[34] = hasLabels;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== actionLabel || $[37] !== isPending || $[38] !== t13) {
      t14 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" size="icon" className="size-6 text-zinc-500 hover:text-zinc-300" aria-label={actionLabel} disabled={isPending}>{t13}</Button></DropdownMenuTrigger>;
      $[36] = actionLabel;
      $[37] = isPending;
      $[38] = t13;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    if ($[40] !== actionLabel || $[41] !== t14) {
      t11 = <BetterTooltip content={actionLabel}>{t14}</BetterTooltip>;
      $[40] = actionLabel;
      $[41] = t14;
      $[42] = t11;
    } else {
      t11 = $[42];
    }
    T3 = DropdownMenuContent;
    t6 = "w-64";
    t7 = "start";
    t8 = "left";
    T2 = Command;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <CommandInput placeholder="Search labels..." className="h-9" autoFocus={true} />;
      $[43] = t5;
    } else {
      t5 = $[43];
    }
    T1 = CommandList;
    if ($[44] !== listStatus) {
      t4 = listStatus ? <div className="px-2 py-1.5 text-sm text-muted-foreground">{listStatus}</div> : <CommandEmpty>No labels found.</CommandEmpty>;
      $[44] = listStatus;
      $[45] = t4;
    } else {
      t4 = $[45];
    }
    T0 = CommandGroup;
    let t15;
    if ($[46] !== appliedLabelNames || $[47] !== handleLabelSelect || $[48] !== pendingChanges) {
      t15 = label_4 => {
        const nameKey_0 = label_4.name.toLowerCase();
        const isSelected = isPendingSelected(pendingChanges, nameKey_0, appliedLabelNames.has(nameKey_0));
        return <CommandItem key={label_4.name} value={label_4.name} keywords={label_4.description ? [label_4.description] : undefined} onSelect={() => handleLabelSelect(label_4.name)} className="flex items-center gap-2"><span className="size-2 shrink-0 rounded-full" style={{
            backgroundColor: `#${toLabelHexColor(label_4.color)}`
          }} /><span className="truncate">{label_4.name}</span><Check className={cn("ml-auto shrink-0", isSelected ? "opacity-100" : "opacity-0")} /></CommandItem>;
      };
      $[46] = appliedLabelNames;
      $[47] = handleLabelSelect;
      $[48] = pendingChanges;
      $[49] = t15;
    } else {
      t15 = $[49];
    }
    t3 = options.map(t15);
    $[4] = appliedLabelNames;
    $[5] = availableLabels;
    $[6] = hasLabels;
    $[7] = installationId;
    $[8] = isError;
    $[9] = isPending;
    $[10] = labels;
    $[11] = number;
    $[12] = onUpdate;
    $[13] = open;
    $[14] = owner;
    $[15] = pendingChanges;
    $[16] = repo;
    $[17] = updateLabels;
    $[18] = T0;
    $[19] = T1;
    $[20] = T2;
    $[21] = T3;
    $[22] = T4;
    $[23] = t10;
    $[24] = t11;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[18];
    T1 = $[19];
    T2 = $[20];
    T3 = $[21];
    T4 = $[22];
    t10 = $[23];
    t11 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t12;
  if ($[50] !== T0 || $[51] !== t3) {
    t12 = <T0>{t3}</T0>;
    $[50] = T0;
    $[51] = t3;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== T1 || $[54] !== t12 || $[55] !== t4) {
    t13 = <T1>{t4}{t12}</T1>;
    $[53] = T1;
    $[54] = t12;
    $[55] = t4;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] !== T2 || $[58] !== t13 || $[59] !== t5) {
    t14 = <T2>{t5}{t13}</T2>;
    $[57] = T2;
    $[58] = t13;
    $[59] = t5;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  let t15;
  if ($[61] !== T3 || $[62] !== t14 || $[63] !== t6 || $[64] !== t7 || $[65] !== t8) {
    t15 = <T3 className={t6} align={t7} side={t8}>{t14}</T3>;
    $[61] = T3;
    $[62] = t14;
    $[63] = t6;
    $[64] = t7;
    $[65] = t8;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== T4 || $[68] !== t10 || $[69] !== t11 || $[70] !== t15 || $[71] !== t9) {
    t16 = <T4 open={t9} onOpenChange={t10}>{t11}{t15}</T4>;
    $[67] = T4;
    $[68] = t10;
    $[69] = t11;
    $[70] = t15;
    $[71] = t9;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  return t16;
}
function _temp3(label_3) {
  return label_3.name;
}
function _temp2(label_1) {
  return {
    name: label_1.name,
    color: label_1.color
  };
}
function _temp(label) {
  return label.name.toLowerCase();
}